/**
 * The running build's identity, and the shape of the manifest the deploy serves.
 *
 * This is the single place that reads the compile-time constants injected by `vue.config.js`.
 * Nothing else should touch `__APP_*__` directly, so there is one place to look when a version
 * looks wrong and one shape for tests to assert against.
 */

import { DateTime } from "luxon";

/** Identity of one build. Emitted as `version.json` and injected as compile-time constants. */
export interface BuildIdentity {
  /** Semantic app version from package.json, e.g. `4.3.1`. */
  version: string;
  /** IRES deploy counter from package.json, e.g. `915`. Shown as "Build 915". */
  buildNumber: number;
  /** Short git commit of the built tree, e.g. `f85c0ead`. */
  commit: string;
  /** ISO 8601 timestamp of when the bundle was built. */
  builtAt: string;
}

/** Deploy-root path of the manifest written by the build. Must never be long-cached. */
export const BUILD_MANIFEST_PATH = "/version.json";

/**
 * Fails loudly for a constant the build config forgot to inject.
 *
 * Reporting a placeholder version would be worse than crashing: the update check would then
 * compare against a value that matches no deploy, and bug reports would carry a fake build.
 */
function missingConstant(name: string): never {
  throw new Error(
    `Build identity: ${name} was not injected at build time. ` +
      "Check the DefinePlugin block in vue.config.js (and the globals block in jest.config.js)."
  );
}

function requireInjectedString(name: string, value: unknown): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    missingConstant(name);
  }

  return value;
}

function requireInjectedInteger(name: string, value: unknown): number {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    missingConstant(name);
  }

  return value;
}

/**
 * Identity of the build currently executing in the browser.
 *
 * Not memoised: it is four constant reads, and memoising would hide a mid-session change in tests.
 * Throws when any constant is absent — see {@link missingConstant}.
 */
export function getRunningBuild(): BuildIdentity {
  // `typeof` guards keep a missing constant a descriptive Error instead of a bare ReferenceError.
  return {
    version: requireInjectedString(
      "__APP_VERSION__",
      typeof __APP_VERSION__ === "undefined" ? undefined : __APP_VERSION__
    ),
    buildNumber: requireInjectedInteger(
      "__APP_BUILD_NUMBER__",
      typeof __APP_BUILD_NUMBER__ === "undefined" ? undefined : __APP_BUILD_NUMBER__
    ),
    commit: requireInjectedString(
      "__APP_COMMIT__",
      typeof __APP_COMMIT__ === "undefined" ? undefined : __APP_COMMIT__
    ),
    builtAt: requireInjectedString(
      "__APP_BUILT_AT__",
      typeof __APP_BUILT_AT__ === "undefined" ? undefined : __APP_BUILT_AT__
    ),
  };
}

/**
 * `builtAt` as `DD.MM.YYYY`, for the "Build 915 (03.08.2026)" label.
 *
 * Fixed format rather than locale-aware on purpose: the date is part of a build identifier that
 * support and release notes quote verbatim, and it read the same in all four locales before the
 * value was injected.
 */
export function getBuildDate(build: BuildIdentity): string {
  return DateTime.fromISO(build.builtAt).toFormat("dd.MM.yyyy");
}

/**
 * Stable key identifying one build, for equality checks and for remembering a dismissal.
 *
 * All three parts are included on purpose: a rollback to an older build still differs from what is
 * running, and the running page should be reloaded for it just the same.
 */
export function getBuildIdentityKey(build: BuildIdentity): string {
  return `${build.buildNumber}|${build.commit}|${build.builtAt}`;
}

/**
 * Validates a parsed `version.json` payload.
 *
 * Returns `null` for anything that is not a complete identity — a hosting error page, a partially
 * written file, an HTML fallback from an SPA rewrite. Callers treat `null` as "no information",
 * never as "an update is available".
 */
export function parseBuildManifest(payload: unknown): BuildIdentity | null {
  if (typeof payload !== "object" || payload === null) {
    return null;
  }

  const candidate = payload as Partial<BuildIdentity>;

  const hasIdentity =
    typeof candidate.version === "string" &&
    candidate.version.trim().length > 0 &&
    typeof candidate.buildNumber === "number" &&
    Number.isInteger(candidate.buildNumber) &&
    typeof candidate.commit === "string" &&
    candidate.commit.trim().length > 0 &&
    typeof candidate.builtAt === "string" &&
    candidate.builtAt.trim().length > 0;

  if (!hasIdentity) {
    return null;
  }

  return {
    version: candidate.version as string,
    buildNumber: candidate.buildNumber as number,
    commit: candidate.commit as string,
    builtAt: candidate.builtAt as string,
  };
}
